import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Marker } from 'react-map-gl/mapbox';

// A single expanding ring (like LiveIndicator's pulse, but a one-shot burst) used to
// briefly point out where a feature is located on the map.
const wave = keyframes`
  0% {
    transform: scale(0.3);
    opacity: 0.8;
  }
  100% {
    transform: scale(2.2);
    opacity: 0;
  }
`;

const Container = styled.div`
  position: relative;
  width: 18px;
  height: 18px;
`;

const fadeOut = keyframes`
  0% { opacity: 1; }
  100% { opacity: 0; }
`;

const Dot = styled.div<{ color: string }>`
  position: absolute;
  inset: 0;
  margin: auto;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ color }) => color};
  animation: ${fadeOut} 0.7s ease-out forwards;
`;

const Ring = styled.div<{ color: string }>`
  position: absolute;
  inset: 0;
  border: 3px solid ${({ color }) => color};
  border-radius: 50%;
  // 'forwards' holds the faded-out final frame so the ring never snaps back to its
  // start size before being unmounted.
  animation: ${wave} 0.7s ease-out forwards;
`;

export interface MapPingHandle {
  ping: (target: { longitude: number; latitude: number; color?: string }) => void;
}

interface PingTarget {
  longitude: number;
  latitude: number;
  color: string;
  nonce: number;
}

// Self-contained locator wave: mount it once and call `ping(...)` via a ref to play a
// one-shot burst at a location. The component owns its own lifetime (re-trigger nonce
// and the timeout that clears the wave), so consumers only supply location and color.
export const MapPing = forwardRef<MapPingHandle>((_props, ref) => {
  const [target, setTarget] = useState<PingTarget | null>(null);
  const nonceRef = useRef(0);

  useImperativeHandle(ref, () => ({
    ping: ({ longitude, latitude, color = '#62bd19' }) => {
      nonceRef.current += 1;
      setTarget({ longitude, latitude, color, nonce: nonceRef.current });
    },
  }));

  useEffect(() => {
    if (target == null) return;
    const timeout = setTimeout(() => setTarget(null), 800);
    return () => clearTimeout(timeout);
  }, [target?.nonce]);

  if (target == null) return null;

  return (
    // A changing key remounts the marker so the CSS animation restarts on every ping,
    // even when the same point is selected repeatedly.
    <Marker
      key={target.nonce}
      longitude={target.longitude}
      latitude={target.latitude}
      anchor="center"
      style={{ pointerEvents: 'none' }}
    >
      <Container>
        <Ring color={target.color} />
        <Dot color={target.color} />
      </Container>
    </Marker>
  );
});
